import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../routes/routes';
import {
  emptyTreeForm,
  isProductLeaf,
  schemaToTreeForm,
  treeFormToSchema,
} from '../../../pages/catalog/product-groups/utils/tree';
import type { ProductGroupTreeForm, TreeChild } from '../../../pages/catalog/product-groups/utils/tree';
import { useProductGroup } from './useProductGroup';

export interface UseProductGroupFormResult {
  groupName: string;
  setGroupName: (next: string) => void;
  treeForm: ProductGroupTreeForm;
  setTreeForm: React.Dispatch<React.SetStateAction<ProductGroupTreeForm>>;
  loading: boolean;
  saving: boolean;
  loadError: string | null;
  isDirty: boolean;
  save: () => Promise<void>;
  navigateAwayWithUnsavedChangesGuard: (target: string) => void;
}

export function useProductGroupForm(groupId: string | undefined): UseProductGroupFormResult {
  const isEditMode = Boolean(groupId);
  const productGroupClient = useProductGroup();
  const navigate = useNavigate();

  const [groupName, setGroupName] = useState('');
  const [treeForm, setTreeForm] = useState<ProductGroupTreeForm>(emptyTreeForm);
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const lastSavedSnapshotRef = useRef<string>(computeFormSnapshot('', emptyTreeForm()));

  const currentSnapshot = computeFormSnapshot(groupName, treeForm);
  const isDirty = currentSnapshot !== lastSavedSnapshotRef.current;

  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isEditMode || !groupId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const fetchedGroup = await productGroupClient.get(groupId);
        if (cancelled) return;
        const initialForm = schemaToTreeForm(fetchedGroup);
        setGroupName(fetchedGroup.groupName);
        setTreeForm(initialForm);
        lastSavedSnapshotRef.current = computeFormSnapshot(fetchedGroup.groupName, initialForm);
      } catch (fetchError) {
        if (cancelled) return;
        setLoadError(fetchError instanceof Error ? fetchError.message : 'Failed to load product group');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId, isEditMode, productGroupClient]);

  const save = useCallback(async () => {
    if (!groupName.trim()) {
      toast.error('Group name is required.');
      return;
    }
    if (treeForm.attributes.some((attribute) => !attribute.trim())) {
      toast.error('Every attribute must have a name (e.g. "color", "size").');
      return;
    }

    let payload;
    try {
      const tree = treeFormToSchema(treeForm);
      payload = { groupName: groupName.trim(), memberAttribute: tree.memberAttribute, members: tree.members };
    } catch (conversionError) {
      toast.error(conversionError instanceof Error ? conversionError.message : 'Invalid group structure');
      return;
    }

    setSaving(true);
    try {
      const saveResult = isEditMode && groupId
        ? await productGroupClient.update(groupId, payload)
        : await productGroupClient.create(payload);
      const successMessage = isEditMode ? 'Product group updated' : `Created "${saveResult.groupName}"`;
      toast.success(successMessage);
      const failedSkus = saveResult.failedSkus ?? [];
      if (failedSkus.length > 0) {
        toast.error(
          `Saved, but ${failedSkus.length} SKU${failedSkus.length === 1 ? '' : 's'} did not finish linking — please retry or fix manually: ${failedSkus.join(', ')}`,
          { duration: 8000 },
        );
      }
      lastSavedSnapshotRef.current = computeFormSnapshot(groupName, treeForm);
      navigate(ROUTES.catalog.productGroups.list);
    } catch (saveError) {
      toast.error(saveError instanceof Error ? saveError.message : 'Failed to save product group', { duration: 8000 });
    } finally {
      setSaving(false);
    }
  }, [groupId, groupName, isEditMode, navigate, productGroupClient, treeForm]);

  const navigateAwayWithUnsavedChangesGuard = useCallback(
    (target: string) => {
      if (isDirty && !window.confirm('You have unsaved changes. Discard them and leave this page?')) return;
      navigate(target);
    },
    [isDirty, navigate],
  );

  return {
    groupName,
    setGroupName,
    treeForm,
    setTreeForm,
    loading,
    saving,
    loadError,
    isDirty,
    save,
    navigateAwayWithUnsavedChangesGuard,
  };
}

function computeFormSnapshot(groupName: string, treeForm: ProductGroupTreeForm): string {
  const stripCosmetic = (children: TreeChild[]): TreeChild[] =>
    children.map((child) => {
      if (isProductLeaf(child)) return { sku: child.sku };
      return { value: child.value, children: stripCosmetic(child.children) };
    });
  return JSON.stringify({
    groupName: groupName.trim(),
    attributes: treeForm.attributes.map((attribute) => attribute.trim()),
    groups: treeForm.groups.map((group) => ({ value: group.value.trim(), children: stripCosmetic(group.children) })),
  });
}
